import type { Approval, ApprovalHistoryEntry, DecisionFor } from '../pages/approvals/approval.js';
import { api } from './api.js';

export type DiscountScope = 'code' | 'customer';
export type DiscountType = 'percent' | 'fixed_amount';

/**
 * A populated relation on a serialized Discount/DiscountCode response —
 * `{ id, label }` or `null` for a deleted/unresolved ref (see backend
 * modules/discounts/serializer.ts, authoritative).
 */
export interface DiscountRef {
  id: string;
  label: string;
}

/**
 * A user document from `GET /users` (the customer picker source) — a DIFFERENT
 * shape from a Discount's `customer` display ref. Users carry `_id` +
 * `first_name`/`last_name`/`email`. Do NOT conflate with `DiscountRef`.
 */
export interface UserRef {
  _id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface Discount {
  id: string;
  scope: DiscountScope;
  discount_type: DiscountType;
  discount_value: number;
  max_discount_minor?: number;
  valid_from?: string;
  valid_until?: string;
  is_active: boolean;
  created_by?: DiscountRef | null;
  created_at?: string;
  updated_at?: string;
  // code-scope only
  name?: string;
  description?: string;
  min_booking_amount_minor?: number;
  total_redemptions_limit?: number;
  redemptions_count?: number;
  usage_per_user?: number;
  car_ids?: string[];
  car_model_ids?: string[];
  rental_window?: { from: string; to: string };
  non_combinable?: boolean;
  // customer-scope only
  customer?: DiscountRef | null;
  note?: string;
  /** 372: absent before #1240 (approved). */
  approval?: Approval;
  /** 372: on the detail read only: what THIS viewer may do about its approval. */
  decision?: DecisionFor;
  approval_history?: ApprovalHistoryEntry[];
}

export interface DiscountCode {
  id: string;
  discount_id: string;
  code: string;
  status: 'active' | 'used' | 'expired' | 'revoked';
  used_by?: DiscountRef | null;
  used_at?: string;
  booking_id?: string | null;
  expires_at?: string;
  created_at?: string;
  updated_at?: string;
  // 212 (#307): distribution, independent of `status` above.
  shared: boolean;
  shared_with?: string;
  shared_by?: DiscountRef | null;
  shared_at?: string;
  // 391 (#1312): the recipient, the related booking, the reason and the approval; null when not shared after 391.
  shared_with_user?: { id: string; full_name: string; email: string } | null;
  share_booking_id?: string | null;
  share_reason?: string | null;
  share_approval?: {
    status: 'pending' | 'approved' | 'rejected';
    decided_by: DiscountRef | null;
    decided_at: string | null;
    reason: string | null;
  } | null;
  /** 391: may THIS caller decide the pending share (the platform's answer, never restated here). */
  share_decision_for?: 'allowed' | 'own' | 'no_permission' | null;
  /** 411 (#1513): the code's cancellation request and its decision; null when never asked. */
  cancellation?: {
    status: 'pending' | 'approved' | 'rejected';
    reason: string;
    asked_by: DiscountRef | null;
    asked_at: string | null;
    decided_by: DiscountRef | null;
    decided_at: string | null;
    reject_reason: string | null;
  } | null;
  /** 411: may THIS caller decide the pending cancellation (a SuperAdmin who did not ask; the platform's answer). */
  cancellation_decision_for?: 'allowed' | 'own' | 'no_permission' | null;
}

/** 411: the codes list's cancellation filter. */
export type CodeCancellationFilter = 'pending' | 'cancelled';

/** 391: the codes list's approval filter. */
export type CodeApprovalFilter = 'pending' | 'approved' | 'rejected' | 'unshared';

/** 391: a user from `GET /users` for the share dialog's customer search. */
export interface ShareCandidate {
  _id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

/** 391: a booking from `GET /bookings?user_id=` for the share dialog's booking picker. */
export interface ShareBookingOption {
  id: string;
  user_id: string;
  status: string;
  pickup_at: string;
}

export interface BulkCodeRowResult {
  code_id: string;
  ok: boolean;
  code?: string;
  message?: string;
}

export interface BulkCodeResponse {
  results: BulkCodeRowResult[];
  summary: { requested: number; succeeded: number; failed: number };
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pages?: number;
  /** 372: discounts waiting for approval, whatever the filter. */
  pending_count?: number;
  /** 411: this campaign's code cancellations waiting for a decision, whatever the filter. */
  cancellation_pending_count?: number;
}

export interface ListDiscountsParams {
  scope?: DiscountScope;
  is_active?: boolean;
  customer_id?: string;
  page?: number;
  limit?: number;
  /** 162: substring of the campaign name. */
  q?: string;
  /** 372: approval state. */
  approval?: 'pending' | 'approved' | 'rejected';
}

function toQuery(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== null) qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/**
 * Typed client for the unified `/discounts` API (044). Both scopes (code +
 * customer) go through the same endpoints; `scope` in the body/query
 * discriminates. Wraps the shared generic `api` client — same transport, auth
 * and error handling as every other admin surface.
 */
export const discountsApi = {
  list: (params: ListDiscountsParams = {}, signal?: AbortSignal) =>
    api.get<Paginated<Discount>>(`/discounts${toQuery(params as Record<string, unknown>)}`, signal),

  get: (id: string) => api.get<Discount>(`/discounts/${id}`),

  /** 372 (#1240): approve, or reject with a reason. */
  decide: (id: string, decision: 'approve' | 'reject', reason?: string) =>
    api.post<Discount>(`/discounts/${id}/${decision}`, reason ? { reason } : {}),

  create: (body: Record<string, unknown>) => api.post<{ id: string }>('/discounts', body),

  update: (id: string, body: Record<string, unknown>) =>
    api.patch<{ id: string }>(`/discounts/${id}`, body),

  remove: (id: string) => api.delete(`/discounts/${id}`),

  listCodes: (
    id: string,
    page = 1,
    limit = 50,
    search?: string,
    approval?: CodeApprovalFilter,
    cancellation?: CodeCancellationFilter,
  ) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    // 164: only when there is something to filter by — a cleared box shows every code (162 FR-004).
    if (search) params.set('q', search);
    // 391: approval filter (replaces 212's shared/unshared).
    if (approval) params.set('approval', approval);
    // 411: cancellation filter.
    if (cancellation) params.set('cancellation', cancellation);
    return api.get<Paginated<DiscountCode>>(`/discounts/${id}/codes?${params.toString()}`);
  },

  generateCodes: (id: string, count: number) =>
    api.post<{ generated: number }>(`/discounts/${id}/codes`, { count }),

  /** 391 (#1312): share codes with one customer, an optional booking of theirs and a reason; each waits for approval. */
  shareCodes: (
    discountId: string,
    codeIds: string[],
    body: { user_id: string; booking_id: string | null; reason: string },
  ) =>
    api.post<BulkCodeResponse>(`/discounts/${discountId}/codes/share`, {
      code_ids: codeIds,
      ...body,
    }),

  /** 391: unshare; the code's history keeps the share. */
  unshareCodes: (discountId: string, codeIds: string[]) =>
    api.post<BulkCodeResponse>(`/discounts/${discountId}/codes/unshare`, { code_ids: codeIds }),

  /** 411 (#1513): ask to cancel unused codes, one reason; each is held until a SuperAdmin decides. */
  askCancelCodes: (discountId: string, codeIds: string[], reason: string) =>
    api.post<BulkCodeResponse>(`/discounts/${discountId}/codes/cancel`, {
      code_ids: codeIds,
      reason,
    }),

  /** 411: a SuperAdmin approves pending cancellations (the codes become Cancelled). */
  approveCancelCodes: (discountId: string, codeIds: string[]) =>
    api.post<BulkCodeResponse>(`/discounts/${discountId}/codes/cancel/approve`, {
      code_ids: codeIds,
    }),

  /** 411: a SuperAdmin rejects pending cancellations with a reason (the codes work again). */
  rejectCancelCodes: (discountId: string, codeIds: string[], reason: string) =>
    api.post<BulkCodeResponse>(`/discounts/${discountId}/codes/cancel/reject`, {
      code_ids: codeIds,
      reason,
    }),

  /** 391: decide the share the page loaded (`ifSharedAt`). */
  approveShare: (discountId: string, codeId: string, ifSharedAt: string) =>
    api.post<DiscountCode>(`/discounts/${discountId}/codes/${codeId}/approve`, {
      if_shared_at: ifSharedAt,
    }),

  rejectShare: (discountId: string, codeId: string, ifSharedAt: string, reason: string) =>
    api.post<DiscountCode>(`/discounts/${discountId}/codes/${codeId}/reject`, {
      if_shared_at: ifSharedAt,
      reason,
    }),

  /** 391: a customer by name, email or phone (326's one box). */
  searchUsers: (q: string) =>
    api.get<{ items: ShareCandidate[] }>(`/users?limit=10&q=${encodeURIComponent(q)}`),

  /** 391: that customer's bookings, optionally narrowed by booking number. */
  customerBookings: (userId: string, q?: string) =>
    api.get<{ items: ShareBookingOption[] }>(
      `/bookings?limit=20&user_id=${encodeURIComponent(userId)}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
    ),

  deactivate: (id: string) => api.post(`/discounts/${id}/deactivate`),

  getActiveForCustomer: (customerId: string) =>
    api.get<Discount | null>(`/discounts/customer/${customerId}/active`),

  /** Reuse the admin users list for the customer-scope customer picker. */
  searchCustomers: (email: string) =>
    api.get<{ items: UserRef[] }>(`/users?limit=10&email=${encodeURIComponent(email)}`),
};
